import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function DashboardPage() {
  const [user, setUser] = useState(null);
  const [myStories, setMyStories] = useState([]);
  const [myChapters, setMyChapters] = useState([]);
  const [stats, setStats] = useState({ stories: 0, chapters: 0, views: 0, likes: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      window.location.href = '/login';
      return;
    }

    const userObj = JSON.parse(userData);
    setUser(userObj);

    try {
      // 获取我的故事
      const storiesRes = await fetch('/api/me/stories?limit=20');
      const storiesData = await storiesRes.json();
      if (storiesData.stories) {
        setMyStories(storiesData.stories);
        // 计算统计
        const totalViews = storiesData.stories.reduce((sum, s) => sum + (s.views || 0), 0);
        const totalLikes = storiesData.stories.reduce((sum, s) => sum + (s.likes || 0), 0);
        setStats({
          stories: storiesData.stories.length,
          chapters: 0, // 需要单独获取
          views: totalViews,
          likes: totalLikes
        });
      }

      // 获取我的章节
      const chaptersRes = await fetch('/api/me/chapters?limit=20');
      const chaptersData = await chaptersRes.json();
      if (chaptersData.chapters) {
        setMyChapters(chaptersData.chapters);
        setStats(prev => ({ ...prev, chapters: chaptersData.chapters.length }));
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-400">加载中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 欢迎区域 */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-6">
        <h1 className="text-2xl font-bold">欢迎回来，{user?.username}!</h1>
        <p className="text-gray-200 mt-1">开始您的创作之旅吧</p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">我的故事</div>
          <div className="text-3xl font-bold text-primary-400 mt-1">{stats.stories}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">章节总数</div>
          <div className="text-3xl font-bold text-green-400 mt-1">{stats.chapters}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">总阅读数</div>
          <div className="text-3xl font-bold text-blue-400 mt-1">{stats.views}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">总点赞数</div>
          <div className="text-3xl font-bold text-red-400 mt-1">{stats.likes}</div>
        </div>
      </div>

      {/* 标签页 */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('stories')}
            className={`flex-1 py-3 px-4 transition-colors ${
              activeTab === 'stories' 
                ? 'bg-gray-700 text-white font-medium' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            📚 我的故事 ({myStories.length})
          </button>
          <button
            onClick={() => setActiveTab('chapters')}
            className={`flex-1 py-3 px-4 transition-colors ${
              activeTab === 'chapters' 
                ? 'bg-gray-700 text-white font-medium' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            📝 章节列表 ({myChapters.length})
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'stories' && (
            <div className="space-y-3">
              {myStories.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">📝</div>
                  <p>还没有创作故事</p>
                  <Link
                    to="/create"
                    className="inline-block mt-4 px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded"
                  >
                    创建第一个故事
                  </Link>
                </div>
              ) : (
                myStories.map(story => (
                  <div
                    key={story.id}
                    className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg truncate">{story.title}</h3>
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                          {story.description}
                        </p>
                        <div className="flex space-x-4 text-xs text-gray-500 mt-2">
                          <span>📖 {story.views || 0} 阅读</span>
                          <span>❤️ {story.likes || 0} 喜欢</span>
                          <span>📅 {new Date(story.updated_at).toLocaleDateString('zh-CN')}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Link
                          to={`/stories/${story.id}`}
                          className="px-3 py-1 bg-primary-600 hover:bg-primary-700 rounded text-sm"
                        >
                          查看
                        </Link>
                        <Link
                          to={`/edit/${story.id}`}
                          className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm"
                        >
                          编辑
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'chapters' && (
            <div className="space-y-3">
              {myChapters.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">📄</div>
                  <p>还没有章节</p>
                </div>
              ) : (
                myChapters.map(chapter => (
                  <div
                    key={chapter.id}
                    className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate">
                          {chapter.story_title} - 第{chapter.chapter_number}章：{chapter.title}
                        </h3>
                        <p className="text-gray-500 text-sm mt-1">
                          {new Date(chapter.created_at).toLocaleString('zh-CN')}
                        </p>
                      </div>
                      <Link
                        to={`/chapter/${chapter.id}`}
                        className="px-3 py-1 bg-primary-600 hover:bg-primary-700 rounded text-sm"
                      >
                        编辑
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
